import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useClickOutside } from '@/hooks/useClickOutside'
import { usePresence } from '@/hooks/usePresence'
import { cn } from '@/lib/cn'

type TriggerProps = {
  open: boolean
  /** Toggles, or with `true` only ever opens (keyboard entry). */
  toggle: (forceOpen?: boolean) => void
  'aria-expanded': boolean
  'aria-haspopup': 'menu' | 'dialog' | 'listbox'
}

type PopoverProps = {
  trigger: (props: TriggerProps) => ReactNode
  children: (close: () => void) => ReactNode
  align?: 'start' | 'end'
  side?: 'bottom' | 'top' | 'right' | 'left'
  className?: string
  kind?: 'menu' | 'dialog' | 'listbox'
  /** Panel at least as wide as the trigger (form selects). */
  matchWidth?: boolean
}

const GAP = 6
const EDGE = 8

/**
 * Floating panel rendered into document.body with fixed positioning. Glass
 * surfaces use backdrop-filter, which creates a stacking context; a panel
 * nested inside one could never rise above later content, so it escapes via
 * a portal and is placed from the trigger's viewport rect. It springs out of
 * the trigger and fades back into it on close.
 */
export function Popover({ trigger, children, align = 'start', side = 'bottom', className, kind = 'menu', matchWidth }: PopoverProps) {
  const [open, setOpen] = useState(false)
  // Parked off-screen (not visibility:hidden) so content can take focus
  // before the layout effect places it — still before the first paint.
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: -9999, left: -9999 })
  const [placedSide, setPlacedSide] = useState(side)
  const presence = usePresence(open, 140)
  const anchor = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    // Keyboard users land back on the trigger instead of <body>.
    const focusInside = panel.current?.contains(document.activeElement)
    setOpen(false)
    if (focusInside) anchor.current?.querySelector<HTMLElement>('button, [tabindex]')?.focus({ preventScroll: true })
  }, [])
  useClickOutside([anchor, panel], open, close)

  useLayoutEffect(() => {
    if (!presence.mounted) return
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect()
      const height = panel.current?.offsetHeight ?? 0
      const width = panel.current?.offsetWidth ?? 0
      if (!rect) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      const next: CSSProperties = { minWidth: matchWidth ? rect.width : undefined }
      let resolved = side

      if (side === 'right' || side === 'left') {
        next.left =
          side === 'right' ? Math.min(rect.right + GAP + 4, vw - width - EDGE) : Math.max(EDGE, rect.left - GAP - 4 - width)
        next.top = Math.max(EDGE, Math.min(rect.top - GAP, vh - height - EDGE))
        next.transformOrigin = `${side === 'right' ? 'left' : 'right'} ${Math.max(12, rect.top + rect.height / 2 - (next.top as number))}px`
      } else {
        const below = rect.bottom + GAP
        const flip = side === 'bottom' ? below + height > vh - EDGE && rect.top - GAP - height > EDGE : true
        resolved = flip ? 'top' : 'bottom'
        next.top = flip ? Math.max(EDGE, rect.top - GAP - height) : below
        next.left =
          align === 'end' ? Math.max(EDGE, rect.right - width) : Math.max(EDGE, Math.min(rect.left, vw - width - EDGE))
        next.transformOrigin = `${align === 'end' ? 'right' : 'left'} ${flip ? 'bottom' : 'top'}`
      }
      setPlacedSide(resolved)
      setStyle(next)
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [presence.mounted, side, align, matchWidth])

  const show = () => {
    if (!open && !presence.mounted) setStyle({ position: 'fixed', top: -9999, left: -9999 })
    setOpen(true)
  }

  return (
    <div ref={anchor} className="relative">
      {trigger({
        open,
        toggle: (forceOpen) => (open && !forceOpen ? close() : show()),
        'aria-expanded': open,
        'aria-haspopup': kind,
      })}
      {presence.mounted &&
        createPortal(
          <div
            ref={panel}
            role={kind === 'listbox' ? undefined : kind}
            data-state={presence.state}
            data-side={placedSide}
            style={style}
            className={cn('glass-menu motion-pop fixed z-[75] min-w-[220px] rounded-[calc(14px*var(--radius-scale))] p-1.5', className)}
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </div>
  )
}

export function MenuItem({
  icon,
  children,
  onSelect,
  trailing,
  danger,
  disabled,
  active,
}: {
  icon?: ReactNode
  children: ReactNode
  onSelect: () => void
  trailing?: ReactNode
  danger?: boolean
  disabled?: boolean
  active?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex h-8.5 w-full items-center gap-2.5 rounded-[calc(9px*var(--radius-scale))] px-2.5 text-left text-[length:calc(13.5px*var(--text-scale))] transition-[background-color,color,transform] duration-100 active:scale-[0.985]',
        danger ? 'text-danger hover:bg-danger-soft' : 'text-fg hover:bg-hover',
        active && 'bg-hover',
        'disabled:opacity-40',
      )}
    >
      {icon && <span className={cn('flex size-4 items-center justify-center', danger ? 'text-danger' : 'text-muted')}>{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing}
    </button>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2.5 pb-1 pt-2 text-[length:calc(11.5px*var(--text-scale))] font-medium uppercase tracking-[0.06em] text-subtle">{children}</div>
}

export function MenuSeparator() {
  return <div className="my-1.5 h-px bg-line" role="separator" />
}
